import React, { useEffect, useState } from "react";
import { CreateCommanWay, GetCommanWay, ListCommanWay, UpdateCommanWay, UploadImageApi } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import Select from "react-select";
import moment from "moment";
import UseToast from "utils/AfToast";
import { Button, Checkbox } from "@heroui/react";
import {
  XMarkIcon,
  PaperClipIcon,
  CalendarDaysIcon,
  DocumentArrowUpIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

const pickupSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 4px",
    minHeight: "36px",
    fontSize: "13px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 8px" }),
  input: (base: any) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: "36px" }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "13px",
    padding: "6px 12px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
    zIndex: 99999,
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "13px" }),
  singleValue: (base: any) => ({ ...base, color: "#1e293b", fontSize: "13px", fontWeight: "500" }),
};

interface ApplyLeaveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: any;
  quotaData?: any;
  isSuperAdmin?: boolean;
}

const ApplyLeaveModal: React.FC<ApplyLeaveModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialData,
  quotaData,
  isSuperAdmin = false,
}) => {
  const isEdit = !!initialData?.id;

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [targetQuota, setTargetQuota] = useState<any>(quotaData);

  const [leaveType, setLeaveType] = useState<any>(null);
  const [fromDate, setFromDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(moment().format("YYYY-MM-DD"));
  const [totalDays, setTotalDays] = useState<number>(1);
  const [isHalfDay, setIsHalfDay] = useState<boolean>(false);
  const [halfDaySession, setHalfDaySession] = useState<string>("FIRST_HALF");
  const [reason, setReason] = useState<string>("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Fetch users if SuperAdmin
  useQuery(
    ["userListForApplyLeaveAdmin"],
    () => ListCommanWay("/admin/user/list", { isActive: true, isThirdParty: false }, { paginate: 2000 }),
    {
      enabled: isOpen && isSuperAdmin,
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const list = res?.data?.data || res?.data?.docs || [];
          const opts = list.map((u: any) => ({
            value: u.id,
            label: `${u.name || u.username} (${u.email || u.mobile || "#" + u.id})`,
          }));
          setUserOptions(opts);
        }
      },
    }
  );

  // If Super Admin selects a target user, fetch that user's quota
  useQuery(
    ["targetUserQuotaForModal", selectedUser?.value],
    () => GetCommanWay(`/admin/leave-quota/user/${selectedUser?.value}`, {}, {}),
    {
      enabled: isSuperAdmin && !!selectedUser?.value,
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          setTargetQuota(res?.data?.quota || res?.data);
        }
      },
    }
  );

  useEffect(() => {
    if (!isSuperAdmin) {
      setTargetQuota(quotaData);
    }
  }, [quotaData, isSuperAdmin]);

  // Recalculate total days when dates or halfDay changes
  useEffect(() => {
    if (isHalfDay) {
      setTotalDays(0.5);
      setToDate(fromDate);
    } else {
      if (fromDate && toDate) {
        const start = moment(fromDate, "YYYY-MM-DD");
        const end = moment(toDate, "YYYY-MM-DD");
        if (end.isBefore(start)) {
          setToDate(fromDate);
          setTotalDays(1);
        } else {
          const diff = end.diff(start, "days") + 1;
          setTotalDays(diff > 0 ? diff : 1);
        }
      }
    }
  }, [fromDate, toDate, isHalfDay]);

  const activeQuota = targetQuota || quotaData || {};

  const getLeaveTypeLabel = (type: string) => {
    switch (type) {
      case "CASUAL":
        return `Casual Leave (CL) • Available: ${activeQuota.casualRemaining ?? 0}`;
      case "SICK":
        return `Sick Leave (SL) • Available: ${activeQuota.sickRemaining ?? 0}`;
      case "PAID":
        return `Paid Leave (PL) • Available: ${activeQuota.paidRemaining ?? 0}`;
      case "COMPENSATORY":
        return `Comp-Off • Available: ${activeQuota.compOffRemaining ?? 0}`;
      case "MATERNITY":
        return `Maternity Leave • Available: ${activeQuota.maternityRemaining ?? 0}`;
      case "UNPAID":
        return `Unpaid Leave / LWP (Salary Deducted)`;
      default:
        return type;
    }
  };

  const leaveTypeOptions = [
    { value: "CASUAL", label: `Casual Leave (CL) • Available: ${activeQuota.casualRemaining ?? 0}` },
    { value: "SICK", label: `Sick Leave (SL) • Available: ${activeQuota.sickRemaining ?? 0}` },
    { value: "PAID", label: `Paid Leave (PL) • Available: ${activeQuota.paidRemaining ?? 0}` },
    ...(activeQuota.isCompOffEligible
      ? [{ value: "COMPENSATORY", label: `Comp-Off • Available: ${activeQuota.compOffRemaining ?? 0}` }]
      : []),
    ...(activeQuota.isMaternityEligible
      ? [{ value: "MATERNITY", label: `Maternity Leave • Available: ${activeQuota.maternityRemaining ?? 0}` }]
      : []),
    { value: "UNPAID", label: `Unpaid Leave / LWP (Salary Deducted)` },
  ];

  // Populate fields on edit or reset on open
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFromDate(initialData.fromDate || moment().format("YYYY-MM-DD"));
        setToDate(initialData.toDate || moment().format("YYYY-MM-DD"));
        setTotalDays(initialData.totalDays || 1);
        setIsHalfDay(!!initialData.isHalfDay);
        setHalfDaySession(initialData.halfDaySession || "FIRST_HALF");
        setReason(initialData.reason || "");
        setAttachments(Array.isArray(initialData.attachments) ? initialData.attachments : initialData.attachments ? [initialData.attachments] : []);
        setLeaveType({ value: initialData.leaveType, label: getLeaveTypeLabel(initialData.leaveType) });
      } else {
        setFromDate(moment().format("YYYY-MM-DD"));
        setToDate(moment().format("YYYY-MM-DD"));
        setTotalDays(1);
        setIsHalfDay(false);
        setHalfDaySession("FIRST_HALF");
        setReason("");
        setAttachments([]);
        setLeaveType({ value: "CASUAL", label: `Casual Leave (CL) • Available: ${activeQuota?.casualRemaining ?? "-"}` });
        setSelectedUser(null);
      }
    }
  }, [isOpen, initialData]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append("files", file);
        formData.append("folder", "leave");

        const response: any = await UploadImageApi(formData);
        if (response?.data?.uploadSuccess?.length) {
          const path = response.data.uploadSuccess[0].path;
          setAttachments((prev) => [...prev, path]);
          UseToast("File uploaded successfully", "success");
        } else if (response?.data?.data?.length) {
          const path = response.data.data[0];
          setAttachments((prev) => [...prev, path]);
          UseToast("File uploaded successfully", "success");
        } else {
          UseToast(response?.message || "File upload failed", "error");
        }
      }
    } catch (err: any) {
      UseToast(err?.message || "File upload failed", "error");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleRemoveAttachment = (indexToRemove: number) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSubmit = async (e?: any) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    if (!leaveType?.value) {
      UseToast("Please select leave type", "warning");
      return;
    }
    if (!fromDate || !toDate) {
      UseToast("Please select valid dates", "warning");
      return;
    }
    if (!reason.trim()) {
      UseToast("Please enter reason for leave", "warning");
      return;
    }

    const payload: any = {
      leaveType: leaveType.value,
      fromDate,
      toDate: isHalfDay ? fromDate : toDate,
      totalDays: isHalfDay ? 0.5 : totalDays,
      isHalfDay,
      halfDaySession: isHalfDay ? halfDaySession : null,
      reason: reason.trim(),
      attachments,
    };

    if (isSuperAdmin && selectedUser?.value) {
      payload.user = selectedUser.value;
    }

    setIsSubmitting(true);
    let res: any;
    try {
      if (isEdit) {
        res = await UpdateCommanWay({
          url: `/admin/leave-application/update/${initialData.id}`,
          data: payload,
        });
      } else {
        res = await CreateCommanWay({
          url: "/admin/leave-application/create",
          data: payload,
        });
      }

      if (res?.status === "SUCCESS") {
        UseToast(isEdit ? "Leave application updated" : "Leave applied successfully!", "success");
        onSuccess();
        onClose();
      } else {
        UseToast(res?.message || "Failed to submit leave application", "error");
      }
    } catch (err: any) {
      UseToast(err || "An error occurred while submitting", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary2 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <CalendarDaysIcon className="w-5 h-5 text-blue-200" />
            <div>
              <h3 className="text-base font-bold">{isEdit ? "Edit Leave Application" : "Apply for Leave"}</h3>
              <p className="text-xs text-blue-100">Submit time-off request</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-blue-200 hover:text-white hover:bg-white/10 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 overflow-y-auto space-y-4 flex-1 text-sm">
          {/* Admin apply on behalf */}
          {isSuperAdmin && !isEdit && (
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Apply on Behalf of Employee <span className="text-slate-400 font-normal">(Admin Only)</span>
              </label>
              <Select
                options={userOptions}
                value={selectedUser}
                onChange={setSelectedUser}
                placeholder="Search employee..."
                isClearable
                styles={pickupSelectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </div>
          )}

          {/* Leave Type Select */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Leave Type <span className="text-red-500">*</span>
            </label>
            <Select
              options={leaveTypeOptions}
              value={leaveType}
              onChange={setLeaveType}
              placeholder="Select Leave Type..."
              styles={pickupSelectStyles}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
            />
          </div>

          {/* Half-Day Checkbox */}
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex items-center justify-between">
            <div>
              <span className="text-sm font-bold text-slate-800 block">Half-Day Leave</span>
              <p className="text-xs text-slate-500 font-medium">Apply for 0.5 day (First or Second Half)</p>
            </div>
            <Checkbox
              isSelected={isHalfDay}
              onValueChange={setIsHalfDay}
              size="md"
              color="primary"
            />
          </div>

          {/* Half-Day Session selector if enabled */}
          {isHalfDay && (
            <div className="bg-blue-50/70 p-3 rounded-xl border border-blue-200/60">
              <label className="block text-xs font-bold text-blue-900 mb-1.5">Select Session:</label>
              <div className="flex gap-5">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
                  <input
                    type="radio"
                    name="halfDaySession"
                    value="FIRST_HALF"
                    checked={halfDaySession === "FIRST_HALF"}
                    onChange={(e) => setHalfDaySession(e.target.value)}
                    className="text-primary focus:ring-primary"
                  />
                  <span>First Half (Morning)</span>
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800 cursor-pointer">
                  <input
                    type="radio"
                    name="halfDaySession"
                    value="SECOND_HALF"
                    checked={halfDaySession === "SECOND_HALF"}
                    onChange={(e) => setHalfDaySession(e.target.value)}
                    className="text-primary focus:ring-primary"
                  />
                  <span>Second Half (Afternoon)</span>
                </label>
              </div>
            </div>
          )}

          {/* Date Picker Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                From Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 bg-white font-medium"
                required
              />
            </div>

            {!isHalfDay ? (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  To Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full h-9 px-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 bg-white font-medium"
                  required
                />
              </div>
            ) : (
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Total Days</label>
                <div className="h-9 px-3 flex items-center text-sm border border-slate-200 rounded-xl bg-slate-100 text-slate-800 font-bold">
                  0.5 Day
                </div>
              </div>
            )}
          </div>

          {!isHalfDay && (
            <div className="flex items-center justify-between px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl">
              <span className="text-xs text-slate-600 font-semibold">Total Duration:</span>
              <span className="text-xs font-extrabold text-primary bg-blue-100 px-2.5 py-1 rounded-full">
                {totalDays} {totalDays === 1 ? "Day" : "Days"}
              </span>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Reason for Leave <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason..."
              className="w-full p-3 text-sm border border-slate-300 rounded-xl outline-none focus:border-blue-600 bg-white resize-none font-medium"
              required
            />
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-bold text-slate-700">Supporting Attachments</label>
              <span className="text-xs text-slate-400">(Optional)</span>
            </div>

            <div className="space-y-2">
              <label className="flex items-center justify-center gap-2 p-3 border border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-all text-xs text-slate-700 font-semibold">
                <DocumentArrowUpIcon className="w-5 h-5 text-primary" />
                <span>{isUploading ? "Uploading..." : "Upload document / image"}</span>
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx"
                />
              </label>

              {attachments.length > 0 && (
                <div className="space-y-1.5">
                  {attachments.map((url, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs"
                    >
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary font-semibold hover:underline truncate max-w-[320px] flex items-center gap-1.5"
                      >
                        <PaperClipIcon className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{url.split("/").pop()}</span>
                      </a>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(idx)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
          <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2.5">
            <Button
              size="sm"
              variant="flat"
              onPress={onClose}
              isDisabled={isSubmitting}
              className="text-xs font-semibold"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              color="primary"
              type="submit"
              onPress={() => handleSubmit()}
              isLoading={isSubmitting}
              className="text-xs font-bold bg-primary text-white shadow"
            >
              {isEdit ? "Update Leave" : "Submit Leave"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplyLeaveModal;
